import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import {
  AppSvgIconComponent,
  SignalButton,
  SignalDropdown,
  SignalTextField,
  State,
} from '@vallift/ngl';
import { BaseComponent } from '@core/base/base-component';
import { MyFormsAPIService } from '@core/api/my-forms/services/my-forms-api/myFormsAPI.service';
import { buildPublishedFormAbsoluteUrl } from '../../../features/forms/utils/form-publish-config';
import type {
  ListMyFormsApiResponse,
  MyFormsFormListItemResponse,
} from '@core/api/my-forms/models';

@Component({
  selector: 'app-my-forms-inbox-page',
  imports: [
    SignalButton,
    SignalDropdown,
    SignalTextField,
    AppSvgIconComponent,
    DatePipe,
  ],
  templateUrl: './my-forms-inbox-page.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyFormsInboxPage extends BaseComponent implements OnInit {
  private readonly myFormsApi = inject(MyFormsAPIService);

  readonly listState = new State<ListMyFormsApiResponse>();
  readonly forms = signal<MyFormsFormListItemResponse[]>([]);
  readonly searchQuery = signal('');
  readonly workspaceFilter = signal('');

  readonly workspaceDropdownItems = computed(() => {
    const byId = new Map<string, string>();
    for (const form of this.forms()) {
      if (!byId.has(form.workspaceId)) {
        byId.set(form.workspaceId, form.workspaceName);
      }
    }
    const workspaces = [...byId.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
    return [{ id: '', name: 'All workspaces' }, ...workspaces];
  });

  readonly filteredForms = computed(() => {
    const query = this.searchQuery().trim().toLowerCase();
    const workspaceId = this.workspaceFilter();
    let items = this.forms();
    if (workspaceId) {
      items = items.filter((form) => form.workspaceId === workspaceId);
    }
    if (!query) {
      return items;
    }
    return items.filter((form) => {
      const haystack = [
        form.name,
        form.workspaceName,
        form.description ?? '',
        form.type,
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  });

  ngOnInit(): void {
    this.loadForms();
  }

  onSearchValueChange(value: unknown): void {
    this.searchQuery.set(typeof value === 'string' ? value : '');
  }

  onWorkspaceFilterChange(value: unknown): void {
    this.workspaceFilter.set(typeof value === 'string' ? value : '');
  }

  openForm(form: MyFormsFormListItemResponse): void {
    const url = buildPublishedFormAbsoluteUrl(form.workspaceSlug, form.slug);
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  private loadForms(): void {
    this.executeRequest({
      state: this.listState,
      request: this.myFormsApi.listMyForms(),
      handleError: true,
      onSuccess: (response) => {
        this.forms.set(response.data.forms ?? []);
      },
    });
  }
}
